import { Order } from "$types";
import { EDBIntegratedOrders } from "$types/EDBIntegratedOrders";
import { TreeProduct } from "$components/JOrderTree/types";

// ===== EDBIntegratedOrders, TreeProduct[] & Orders[] converters ===== //

function treeProductsToOrders(treeProducts: TreeProduct[]): Order[] {
  if (!treeProducts) return [];
  // Get all unique orders & suborders, without suborders and suborderProducts filled
  const allOrders = treeProducts.reduce((allOrders, item) => {
    if (!findByRef(allOrders, item.order)) {
      allOrders.push({
        ...item.order,
        suborders: [],
      });
    }
    return allOrders;
  }, []);

  const allSuborders = treeProducts.reduce((allSuborders, item) => {
    if (!findByRef(allSuborders, item.suborder)) {
      allSuborders.push({
        ...item.suborder,
        suborderProducts: [],
      });
    }
    return allSuborders;
  }, []);

  // Reconstruct the orders by filling the suborders and suborderProducts
  const formattedOrders = [...allOrders];
  formattedOrders.forEach((order) => {
    // Add suborders to each order
    allSuborders.forEach((suborder) => {
      if (suborder.orderReference === order.reference) {
        order.suborders.push(suborder);
      }
    });
    // Add product to each suborder
    treeProducts.forEach((item) => {
      order.suborders.forEach((suborder) => {
        if (suborder.reference === item.suborder.reference) {
          suborder.suborderProducts.push(item.product);
        }
      });
    });
  });

  return formattedOrders;
}

function edbIoToTreeProducts(
  integratedOrders: EDBIntegratedOrders
): TreeProduct[] {
  return (
    integratedOrders?.orderProducts
      ?.map((orderProduct) => {
        const {
          order,
          suborderId,
          suborderProductId,
          quantity,
          initialQuantity,
          integrableQuantity,
        } = orderProduct;
        const suborder = order?.suborders?.find((s) => s.id === suborderId);
        if (!suborder) return null;

        const product = suborder?.suborderProducts?.find(
          (p) => p.id === suborderProductId
        );
        if (!product) return null;

        // Ensure quantities are properly set, fallback to product.quantity if missing
        const finalQuantity =
          quantity !== undefined && quantity !== null
            ? quantity
            : product.quantity || 0;
        const finalIntegrableQuantity =
          integrableQuantity !== undefined && integrableQuantity !== null
            ? integrableQuantity
            : product.integrableQuantity !== undefined &&
                product.integrableQuantity !== null
              ? product.integrableQuantity
              : product.quantity || 0;

        const treeProduct: TreeProduct = {
          product: {
            id: product.id,
            name: product.name,
            quantity: finalQuantity,
            integrableQuantity: finalIntegrableQuantity,
            // == null works for both null and undefined
            initialQuantity:
              initialQuantity == null ? finalQuantity : initialQuantity,
            reference: product.reference,
            unit: product.unit,
          },
          suborder: {
            id: suborder.id,
            orderReference: order.reference,
            reference: suborder.reference,
            dockyard: suborder.dockyard,
            project: suborder.project,
          },
          order: {
            id: order.id,
            reference: order.reference,
            suborders: order.suborders,
          },
        };
        return treeProduct;
      })
      .filter(Boolean) || []
  );
}

function ordersToEdbIo(orders: Order[]): EDBIntegratedOrders {
  return {
    orderProducts: orders.map((order) => {
      // When converting an order to an EDBIo, the order only contains one product in one suborder.
      const product = order.suborders[0].suborderProducts[0];
      // Use integrableQuantity if available, otherwise fall back to quantity
      // This ensures we always have a valid quantity value
      const integrableQuantity =
        product.integrableQuantity !== undefined &&
        product.integrableQuantity !== null
          ? product.integrableQuantity
          : product.quantity || 0;
      return {
        order,
        suborderId: order.suborders[0].id,
        suborderProductId: product.id,
        quantity: integrableQuantity,
        integrableQuantity,
        initialQuantity: 0,
      };
    }),
  };
}

function edbIoToOrders(integratedOrders: EDBIntegratedOrders): Order[] {
  const treeProducts = edbIoToTreeProducts(integratedOrders);
  const orders = treeProductsToOrders(treeProducts);
  return orders;
}

// ===== End converters ===== //

/**
 * Remove a product from an integratedOrders object
 */
function filterProduct(
  integratedOrders: EDBIntegratedOrders,
  treeProduct: TreeProduct
): EDBIntegratedOrders {
  const { order, product, suborder } = treeProduct;
  const { orderProducts } = integratedOrders;

  return {
    ...integratedOrders,
    orderProducts: orderProducts.filter(
      (orderProduct) =>
        orderProduct.suborderProductId !== product.id ||
        orderProduct.suborderId !== suborder.id ||
        orderProduct.order.id !== order.id
    ),
  };
}

function updateProductQuantity(
  integratedOrders: EDBIntegratedOrders,
  treeProduct: TreeProduct,
  newQuantity: number
): EDBIntegratedOrders {
  const { order, product, suborder } = treeProduct;
  const { orderProducts } = integratedOrders;

  return {
    ...integratedOrders,
    orderProducts: orderProducts.map((orderProduct) => {
      if (
        orderProduct.suborderProductId === product.id &&
        orderProduct.suborderId === suborder.id &&
        orderProduct.order.id === order.id
      ) {
        return {
          ...orderProduct,
          quantity: newQuantity,
          initialQuantity: product.initialQuantity,
        };
      }
      return orderProduct;
    }),
  };
}

function findByRef(collection, toFindItem) {
  return collection.find((item) => item.reference === toFindItem.reference);
}
function filterOutByRef(collection, toFindItem) {
  return collection.filter((item) => item.reference !== toFindItem.reference);
}

export {
  treeProductsToOrders,
  edbIoToTreeProducts,
  edbIoToOrders,
  findByRef,
  filterOutByRef,
  filterProduct,
  updateProductQuantity,
  ordersToEdbIo,
};
